import { useContext } from "react";
import { useMutation, useQueryClient } from "react-query";

import { UserContext } from "@/components/User";

import { submitTimeReport, SubmitTimeReportDto, updateTimeReport, updateTimeReportProjectItem } from "../service";
import { TimeReport, TimeReportGuid, TimeReportProjectItem } from "../types";
import { TimeReportProjectItemPath, transformTimeReportProjectItem } from "../utils";
import { buildTimeReportQueryKey } from "./buildTimeReportQueryKey";

export interface UpdateProjectItemMutationVars {
  path: TimeReportProjectItemPath;
  newTimeReport: TimeReport;
  newItem: TimeReportProjectItem;
}

export const useTimeReportMutations = (guid: TimeReportGuid) => {
  const { contextCompany, contextConsultantProfileId } = useContext(UserContext);

  const queryKey = buildTimeReportQueryKey(contextConsultantProfileId, guid);

  const queryClient = useQueryClient();

  const updateMutation = useMutation(
    (vars: TimeReport) => updateTimeReport({ timeReportGuid: guid, companyId: contextCompany?.id }, vars),
    {
      onSuccess: data => {
        queryClient.setQueryData<TimeReport>(queryKey, data);
      },
    },
  );

  const updateProjectItemMutation = useMutation(
    async (vars: UpdateProjectItemMutationVars) => {
      const { path, newTimeReport, newItem } = vars;

      return await updateTimeReportProjectItem(
        {
          timeReportGuid: guid,
          projectGuid: path.projectGuid,
          timeReportLastReadDate: newTimeReport.lastReadDate!,
          companyId: contextCompany?.id,
        },
        newItem,
      );
    },
    {
      onSuccess: (data, vars) => {
        const resultTimeReport = transformTimeReportProjectItem(
          {
            ...vars.newTimeReport,
            lastReadDate: data.lastModificationDate,
          },
          vars.path,
          item => ({ ...item, id: data.itemId }),
        );

        queryClient.setQueryData<TimeReport>(queryKey, resultTimeReport);
      },
    },
  );

  const submitMutation = useMutation(
    (vars: SubmitTimeReportDto) => submitTimeReport({ timeReportGuid: guid, companyId: contextCompany?.id }, vars),
    {
      onSuccess: data => {
        queryClient.setQueryData<TimeReport>(queryKey, data);
      },
    },
  );

  return {
    update: updateMutation.mutateAsync,
    updateProjectItem: updateProjectItemMutation.mutateAsync,
    submit: submitMutation.mutateAsync,
  };
};
